/**
 * Postkorb mock primitives — skeleton PDF pages and cut slots.
 */
import { ld } from "@/lib/theme/light-dark";
import { Scissors } from "lucide-react";
import type { ReactNode } from "react";

/** Simplified A4 skeleton — gray lines, no real PDF content. */
export function PostkorbSkeletonPage(props: {
  readonly variant: number;
}): ReactNode {
  const lineWidths: readonly number[] =
    props.variant === 0
      ? [88, 72, 92, 64, 80]
      : props.variant === 1
        ? [76, 90, 68, 84]
        : [82, 70, 88, 58, 74, 66];

  return (
    <div
      style={{
        width: 74,
        height: 98,
        flexShrink: 0,
        borderRadius: 6,
        border: `1px solid ${ld("#E4E6EA", "#35383c")}`,
        background: ld("#fff", "#181b1f"),
        boxShadow: `0 4px 14px -8px ${ld("rgba(11,15,25,0.22)", "rgba(0,0,0,0.38)")}`,
        padding: "10px 8px",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 5,
      }}
    >
      {lineWidths.map((widthPct, index) => (
        <span
          key={index}
          style={{
            display: "block",
            height: 4,
            borderRadius: 2,
            width: `${String(widthPct)}%`,
            background:
              index === 0 ? ld("rgba(11,15,25,0.14)", "rgba(250,250,250,0.14)") : ld("rgba(11,15,25,0.07)", "rgba(250,250,250,0.07)"),
          }}
        />
      ))}
    </div>
  );
}

/** Cut hot-zone between skeleton pages — scissors on hover. */
export function PostkorbCutSlot(props: {
  readonly demoActive?: boolean;
}): ReactNode {
  const demoActive = props.demoActive === true;
  return (
    <div
      className="mkhero-postkorb-cut-group"
      data-demo-active={demoActive ? "true" : "false"}
      style={{
        position: "relative",
        width: 22,
        flexShrink: 0,
        alignSelf: "stretch",
        minHeight: 98,
      }}
    >
      <span
        aria-hidden
        className="mkhero-postkorb-cut-line"
        data-active={demoActive ? "true" : "false"}
      />
      <span aria-hidden className="mkhero-postkorb-cut-btn">
        <Scissors width={12} height={12} strokeWidth={2} />
      </span>
    </div>
  );
}

export function PostkorbPageGroup(props: {
  readonly label: string;
  readonly pageCount: number;
  readonly showCutAfterIndices: readonly number[];
}): ReactNode {
  const pages: number[] = Array.from({ length: props.pageCount }, (_, i) => i);

  return (
    <section style={{ marginBottom: 22 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: ld("rgba(11,15,25,0.78)", "rgba(250,250,250,0.78)"),
          marginBottom: 10,
        }}
      >
        {props.label}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          flexWrap: "nowrap",
        }}
      >
        {pages.map((pageIndex, arrayIndex) => (
          <span key={pageIndex} style={{ display: "contents" }}>
            <PostkorbSkeletonPage variant={pageIndex % 3} />
            {arrayIndex < pages.length - 1 ? (
              <PostkorbCutSlot
                demoActive={props.showCutAfterIndices.includes(pageIndex)}
              />
            ) : null}
          </span>
        ))}
      </div>
    </section>
  );
}
